import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  CalendarCheck2, Unlink, RefreshCw, CheckCircle2, AlertCircle,
  Clock, ShieldCheck, Upload, Copy, Save, ArrowDownUp,
} from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Field } from "../../components/admin/Fields";
import CardCollapse from "./CardCollapse";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
}

export default function GoogleCalendarSync() {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const { data: status } = useQuery({
    queryKey: ["admin", "gcal", "status"],
    queryFn: () => api.get("/admin/gcal/status").then((r) => r.data),
    refetchInterval: 20000,
  });

  const [saJson, setSaJson] = useState("");
  const [calendarId, setCalendarId] = useState("");
  const [savingCreds, setSavingCreds] = useState(false);
  const [testing, setTesting] = useState(false);
  const [busy, setBusy] = useState(false);

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setSaJson(String(reader.result || ""));
    reader.onerror = () => toast.error("Dosya okunamadı");
    reader.readAsText(f);
    e.target.value = "";
  };

  const saveCreds = async () => {
    if (!saJson.trim() && status?.sa_source !== "drive") {
      toast.error("Önce Service Account JSON yapıştır");
      return;
    }
    setSavingCreds(true);
    try {
      const body = { calendar_id: calendarId.trim() || null };
      if (saJson.trim()) body.sa_json = saJson.trim();
      // When reusing the Drive key, only persist calendar_id via /config
      if (!body.sa_json) {
        await api.put("/admin/gcal/config", { calendar_id: body.calendar_id || "primary" });
      } else {
        await api.put("/admin/gcal/credentials", body);
      }
      toast.success("Kaydedildi");
      setSaJson(""); setCalendarId("");
      qc.invalidateQueries({ queryKey: ["admin", "gcal", "status"] });
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSavingCreds(false); }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/gcal/test");
      toast.success(`Takvim bağlı · ${data.summary || data.calendar_id}`);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setTesting(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Google Takvim bağlantısını kaldırmak istiyor musun? Takvimdeki etkinlikler kalır ancak artık senkron olmaz.")) return;
    setBusy(true);
    try {
      await api.post("/admin/gcal/disconnect");
      toast.success("Bağlantı kaldırıldı");
      qc.invalidateQueries({ queryKey: ["admin", "gcal", "status"] });
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const runSync = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/gcal/sync");
      toast.success(`Senkron tamam — ${data.imported || 0} yeni / ${data.updated || 0} güncellendi / ${data.deleted || 0} silindi`);
      qc.invalidateQueries({ queryKey: ["admin", "gcal", "status"] });
      qc.invalidateQueries({ queryKey: ["admin", "events"] });
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const copySaEmail = () => {
    if (!status?.sa_email) return;
    navigator.clipboard.writeText(status.sa_email);
    toast.success("Service Account e-postası panoya kopyalandı");
  };

  if (!status) return <div className="bg-white border rounded-xl p-5 text-sm text-stone-400">Google Takvim durumu yükleniyor…</div>;

  const connected = !!status.connected;
  const reusingDrive = connected && status.sa_source === "drive";
  const chip = (
    <span className={`text-[11px] uppercase tracking-widest font-semibold px-2 py-1 rounded ${connected ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-600"}`}>
      {connected ? "Bağlı" : "Bağlı değil"}
    </span>
  );

  return (
    <CardCollapse
      testid="integration-gcal"
      icon={<CalendarCheck2 size={16} />}
      title="Google Takvim Senkron"
      description="Service Account ile — takvim siz paylaşana kadar erişilemez; başka takvimlere dokunulamaz."
      chip={chip}
    >
      <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 text-xs text-emerald-900 flex gap-2">
        <ShieldCheck size={16} className="shrink-0 mt-0.5" />
        <div>
          <strong>Güvenlik:</strong> SA sadece sizin paylaştığınız takvime bakar. Takvim ID'niz, SA e-postanız şifreli saklanır. Erişimi dilediğiniz an Google Takvim'de "Paylaş" ayarından iptal edebilirsiniz.
        </div>
      </div>

      {!connected && (
        <details className="border rounded-lg bg-stone-50/50" open>
          <summary className="cursor-pointer px-4 py-3 text-sm font-medium flex items-center gap-2">
            <AlertCircle size={15} className="text-amber-600" />Kurulum (3 adım)
          </summary>
          <div className="p-4 pt-0 space-y-3">
            <ol className="text-xs text-stone-700 list-decimal pl-5 space-y-2">
              <li>Google Cloud'da bir <a className="underline" href="https://console.cloud.google.com/iam-admin/serviceaccounts" target="_blank" rel="noopener noreferrer">Service Account</a> oluştur ve JSON anahtarını indir (Drive kartı için oluşturduğun aynısı kullanılabilir — bu durumda tekrar yüklemen gerekmez)</li>
              <li><a className="underline" href="https://console.cloud.google.com/apis/library/calendar-json.googleapis.com" target="_blank" rel="noopener noreferrer">Google Calendar API</a>'yi Enable et</li>
              <li>Google Takvim'inde senkronlamak istediğin takvimi seç → <strong>Settings and sharing</strong> → <strong>Share with specific people</strong> → SA e-postasını ekle (<em>Make changes to events</em>) → aşağıdaki alanlara takvim ID'sini yapıştır (<code className="bg-white border px-1 rounded">Integrate calendar &rsaquo; Calendar ID</code>)</li>
            </ol>
            <div className="space-y-2 border-t pt-3">
              <Field label="Service Account JSON" hint="Drive kartına yüklediysen boş bırak — aynı anahtar kullanılır.">
                <textarea
                  value={saJson}
                  onChange={(e) => setSaJson(e.target.value)}
                  placeholder='{"type":"service_account", …}'
                  rows={4}
                  className="w-full border rounded-md p-2 text-xs font-mono"
                  data-testid="gcal-sa-json"
                />
              </Field>
              <div className="flex items-center gap-2">
                <input ref={fileRef} type="file" accept="application/json,.json" className="hidden" onChange={onPickFile} data-testid="gcal-sa-file" />
                <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()}><Upload size={13} className="mr-1" />Dosyadan seç</Button>
              </div>
              <Field label="Takvim ID'si" hint="Takvim Ayarları → 'Takvim entegre et' bölümünde yazar">
                <Input value={calendarId} onChange={(e) => setCalendarId(e.target.value)} placeholder="xxx@group.calendar.google.com" data-testid="gcal-calendar-id" />
              </Field>
              <Button type="button" size="sm" onClick={saveCreds} disabled={savingCreds} data-testid="gcal-save-credentials">
                {savingCreds ? <RefreshCw size={14} className="mr-1 animate-spin" /> : <Save size={14} className="mr-1" />}
                Kaydet ve bağlan
              </Button>
            </div>
          </div>
        </details>
      )}

      {connected && (
        <div className="border rounded-lg p-4 space-y-3" data-testid="gcal-connected-box">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <p className="text-sm font-medium flex items-center gap-2"><CheckCircle2 size={15} className="text-emerald-600" />Service Account bağlı
                {reusingDrive && <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded bg-blue-100 text-blue-800">Drive kartından ödünç</span>}
              </p>
              <p className="text-xs text-stone-600 mt-1 flex items-center gap-1">
                <span className="font-mono bg-stone-100 border rounded px-1.5 py-0.5">{status.sa_email}</span>
                <button type="button" onClick={copySaEmail} className="text-stone-500 hover:text-stone-900" data-testid="gcal-copy-email" title="Kopyala"><Copy size={11} /></button>
              </p>
              <p className="text-[11px] text-stone-500 mt-0.5">Takvim: <code className="bg-stone-100 px-1 rounded">{status.calendar_id}</code></p>
            </div>
            <div className="flex gap-2">
              <Button type="button" size="sm" variant="outline" onClick={testConnection} disabled={testing} data-testid="gcal-test"><RefreshCw size={13} className={`mr-1 ${testing ? "animate-spin" : ""}`} />Test et</Button>
              <Button type="button" size="sm" variant="outline" onClick={runSync} disabled={busy} data-testid="gcal-sync-now"><ArrowDownUp size={13} className="mr-1" />Şimdi senkronla</Button>
              <Button type="button" size="sm" variant="outline" onClick={disconnect} disabled={busy} data-testid="gcal-disconnect"><Unlink size={13} className="mr-1" />Bağlantıyı kaldır</Button>
            </div>
          </div>

          <div className="border-t pt-3 space-y-2">
            <Field label="Takvim ID (değiştirmek için güncelle)">
              <div className="flex gap-2">
                <Input value={calendarId} onChange={(e) => setCalendarId(e.target.value)} placeholder={status.calendar_id} data-testid="gcal-calendar-id-edit" />
                <Button type="button" size="sm" variant="outline" onClick={saveCreds} disabled={savingCreds || !calendarId.trim()}><Save size={13} className="mr-1" />Kaydet</Button>
              </div>
            </Field>
          </div>

          <div className="grid sm:grid-cols-4 gap-2 text-xs border-t pt-3">
            <div className="bg-stone-50 border rounded p-2">
              <p className="text-stone-500 uppercase tracking-wide text-[10px] mb-0.5">Son senkron</p>
              <p className="font-medium flex items-center gap-1"><Clock size={11} />{fmtDate(status.last_sync_at)}</p>
            </div>
            <div className="bg-stone-50 border rounded p-2">
              <p className="text-stone-500 uppercase tracking-wide text-[10px] mb-0.5">Yeni</p>
              <p className="font-medium">{status.last_sync_imported ?? "—"}</p>
            </div>
            <div className="bg-stone-50 border rounded p-2">
              <p className="text-stone-500 uppercase tracking-wide text-[10px] mb-0.5">Güncellenen</p>
              <p className="font-medium">{status.last_sync_updated ?? "—"}</p>
            </div>
            <div className="bg-stone-50 border rounded p-2">
              <p className="text-stone-500 uppercase tracking-wide text-[10px] mb-0.5">Silinen</p>
              <p className="font-medium">{status.last_sync_deleted ?? "—"}</p>
            </div>
          </div>
          {status.last_sync_error && <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">⚠ {status.last_sync_error}</p>}
        </div>
      )}
    </CardCollapse>
  );
}
